import { LangText } from '@/components';
import { preFixImgUrl } from '@/utils';
import {
  COUPON_CATEGORY,
  TEMP_COUPON_STORES,
  type TempCouponStore,
} from '@/pages/Coupon/List/couponListInfo';
import { useMemo, useState } from 'react';
import emptyCouponImg from '@/assets/svg/empty-coupon.svg';
import { useNavigate } from 'react-router-dom';

const COUPON_COVER_IMG = preFixImgUrl('0c77df0bb817b255cc31629d5c337c4b.png');

const COUPON_TABS = [
  { id: 'shopping', labelKey: 'home.coupon.tabs.shopping', primaryCategory: COUPON_CATEGORY.shopping.zh_CN },
  { id: 'food', labelKey: 'home.coupon.tabs.food', primaryCategory: '餐饮美食' },
  { id: 'transport', labelKey: 'home.coupon.tabs.transport', primaryCategory: '交通出行' },
  { id: 'hotel', labelKey: 'home.coupon.tabs.hotel', primaryCategory: '住宿娱乐' },
] as const;

type CouponTabId = typeof COUPON_TABS[number]['id'];

const SectionTitle = () => {
  return (
    <div className="flex flex-col items-center">
      <LangText lngKey="home.coupon.title" className="text-[36px] font-bold leading-[50px] text-[#000]" />
      <span className="mt-[16px] h-[4px] w-[108px] rounded-full bg-primary" />
    </div>
  );
};

const CouponCard = ({ coupon }: { coupon: TempCouponStore }) => {
  const discount = coupon.discount?.zh_CN ?? 'home.coupon.defaultDiscount';
  const navigate = useNavigate();
  const openCouponDetail = () => navigate(`/coupon-detail?id=${coupon.id}`);
  return (
    <div className="group relative h-[192px] overflow-hidden rounded-[12px] bg-white" onClick={openCouponDetail} onKeyDown={(event) => {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      event.preventDefault();
      openCouponDetail();
    }}>
      <img
        src={preFixImgUrl(coupon.listImageFile)}
        alt={coupon.merchantName}
        className="object-cover absolute inset-0 w-full h-full transition-transform duration-500 ease-out group-hover:scale-110"
        draggable={false}
      />
      <img
        src={COUPON_COVER_IMG}
        alt=""
        className="pointer-events-none absolute left-0 top-0 z-10 h-full w-[238px] object-fill"
        draggable={false}
      />
      <div className="absolute left-[24px] top-[14px] z-20 flex h-full flex-col items-start">
        <LangText
          lngKey={COUPON_TABS[0].labelKey}
          className="rounded-full bg-[#9B62FF] px-[9px] text-[12px] leading-[20px] text-white"
        />
        <LangText lngKey={coupon.merchantName} className="mt-[10px] text-[20px] font-bold leading-[28px] text-white" />
        <LangText lngKey={discount} className="mt-[4px] text-[18px] font-bold leading-[25px] text-white" />
        <button
          type="button"
          className="mt-[35px] h-[30px] rounded-full bg-white px-[14px] text-[14px] font-bold text-[#000]"
        >
          <LangText lngKey="common.action.claimFree" />
        </button>
      </div>
      <span className="pointer-events-none absolute left-[-10px] top-1/2 z-30 h-[20px] w-[20px] -translate-y-1/2 rounded-full bg-white" />
      <span className="pointer-events-none absolute right-[-10px] top-1/2 z-30 h-[20px] w-[20px] -translate-y-1/2 rounded-full bg-white" />
    </div>
  );
};

const CouponEmpty = () => {
  return (
    <div className="mt-[24px] flex h-[192px] items-center justify-center rounded-[12px] bg-white">
      <div className="flex items-center gap-[36px]">
        <img src={emptyCouponImg} alt="" className="h-[105px] w-[130px]" draggable={false} />
        <LangText lngKey="home.coupon.empty" className="text-[20px] font-bold leading-[28px] text-[#000]" />
      </div>
    </div>
  );
};

export const NearbyCoupons = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<CouponTabId>('shopping');

  const activeCategory = COUPON_TABS.find((tab) => tab.id === activeTab)?.primaryCategory;

  const coupons = useMemo(
    () =>
      TEMP_COUPON_STORES.filter((store) => store.primaryCategory.zh_CN === activeCategory).slice(0, 3),
    [activeCategory],
  );

  return (
    <section className="mx-auto w-[1200px] pt-[96px]">
      <SectionTitle />
      <div className="mt-[82px] flex items-center justify-between">
        <div className="flex items-center gap-[12px]">
          {COUPON_TABS.map((tab) => {
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={`h-[40px] min-w-[100px] rounded-[8px] px-[18px] text-[16px] font-medium transition-colors ${
                  isActive
                    ? 'border border-primary bg-secondary text-primary'
                    : 'border border-transparent bg-[#F2F2F2] text-[#979797]'
                }`}
              >
                <LangText lngKey={tab.labelKey} />
              </button>
            );
          })}
        </div>
        <button
          type="button"
          className="h-[40px] rounded-full bg-primary px-[24px] text-[16px] font-medium text-white transition-opacity hover:opacity-90"
          onClick={() => navigate('/coupon-list')}
        >
          <LangText lngKey="common.action.viewAll" />
        </button>
      </div>
      {coupons.length > 0 ? (
        <div className="mt-[24px] grid grid-cols-3 gap-[16px]">
          {coupons.map((coupon) => (
            <CouponCard key={coupon.id} coupon={coupon} />
          ))}
        </div>
      ) : (
        <CouponEmpty />
      )}
    </section>
  );
};
